import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Button, CircularProgress, Collapse, Drawer, FormControlLabel, IconButton, InputAdornment, MenuItem, Switch, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LocationPicker from '../map/LocationPicker';
import SurveyFields from './SurveyFields';
import { useTranslation } from 'react-i18next';
import ClientPicker from '../common/ClientPicker';
import { StatusSegmented } from '../common/ui';
import JobService from '../../services/JobService';
import CategoryService from '../../services/CategoryService';
import UserService from '../../services/UserService';
import { errorMessage, fieldErrors } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { hasInvestor, hasParcelNumbers, scheduledLabel } from '../../utils/workflows';

const EMPTY = {
    client: null, number: '', categoryId: '', title: '', description: '', status: 'RECEIVED',
    scheduledDate: '', scheduledStart: '', scheduledEnd: '', processedOn: '', price: '', parcelNumber: '', cadastralZone: '', municipality: '', ownerName: '', coOwners: [], propertySheet: '', investor: '', parentParcel: '', newParcels: '', location: null,
    cadastreCaseNumber: '', submittedOn: '', includeParcelling: false, assignees: [],
};

/**
 * Right-side drawer to create or edit a job (Plankton's WorkspaceDrawer pattern).
 * `defaults` can pre-fill fields, e.g. { client } from the client page or { scheduledDate } from the planner.
 * `section` ('details' incl. description | 'survey') edits one box of the job page in a narrow drawer: the other
 * groups stay mounted but hidden, so the save is the same full update with the same checks.
 */
export default function JobFormDrawer({ open, job, defaults, money, section, onClose, onSaved }) {
    const hide = (...names) => (!section || names.includes(section) ? {} : { display: 'none' });
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { isAdmin } = useAuth();
    const [form, setForm] = useState(EMPTY);
    const initFor = useRef(null);
    const [errors, setErrors] = useState({});
    const [categories, setCategories] = useState([]);
    const [team, setTeam] = useState([]);
    const [nextNumber, setNextNumber] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showLocation, setShowLocation] = useState(false);

    useEffect(() => {
        if (!open) { initFor.current = null; lastAutoTitle.current = ''; return; }
        // The same job again (e.g. reloaded after the prices were unlocked): keep the edits, only fill in the price.
        const key = job?.id ?? 'new';
        if (initFor.current === key) {
            if (job?.price != null) setForm(f => (f.price === '' ? { ...f, price: job.price } : f));
            return;
        }
        initFor.current = key;
        CategoryService.list().then(setCategories).catch(() => {});
        UserService.directory().then(setTeam).catch(() => {});
        // Case numbers are optional; the app only suggests the next free one (one click to use it).
        setNextNumber(null);
        if (!job?.number) JobService.nextNumber().then(setNextNumber).catch(() => {});
        setErrors({});
        if (job) {
            setForm({
                client: job.client, number: job.number ?? '', categoryId: job.category?.id ?? '', title: job.title, description: job.description ?? '',
                status: job.status, scheduledDate: job.scheduledDate ?? '',
                scheduledStart: job.scheduledStart ?? '', scheduledEnd: job.scheduledEnd ?? '', processedOn: job.processedOn ?? '', price: job.price ?? '',
                parcelNumber: job.parcelNumber ?? '', cadastralZone: job.cadastralZone ?? '', municipality: job.municipality ?? '',
                ownerName: job.ownerName ?? '', coOwners: job.coOwners || [], propertySheet: job.propertySheet ?? '', investor: job.investor ?? '',
                parentParcel: job.parentParcel ?? '', newParcels: job.newParcels ?? '',
                location: job.latitude != null ? { lat: job.latitude, lng: job.longitude } : null,
                cadastreCaseNumber: job.cadastreCaseNumber ?? '', submittedOn: job.steps?.SUBMITTED ?? '', includeParcelling: !!job.includeParcelling,
                assignees: job.assignees || [],
            });
            setShowLocation(job.latitude != null);
        } else {
            setForm({ ...EMPTY, ...(defaults || {}) });
            setShowLocation(false);
        }
    }, [open, job, defaults]);

    const set = (k) => (e) => {
        setForm(f => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors(x => ({ ...x, [k]: undefined }));
    };

    const save = async () => {
        const v = {};
        if (!form.client) v.clientId = t('jobs.errors.clientRequired');
        if (!form.title.trim()) v.title = t('validation.required');
        if (form.price !== '' && Number(form.price) < 0) v.price = t('jobs.errors.priceNegative');
        if (form.scheduledStart && form.scheduledEnd && form.scheduledEnd <= form.scheduledStart) v.scheduledEnd = t('jobs.errors.timeRange');
        setErrors(v);
        if (Object.keys(v).length) return;

        const payload = {
            clientId: form.client.id,
            number: form.number.trim() || null,
            categoryId: form.categoryId || null,
            title: form.title,
            description: form.description,
            status: form.status,
            scheduledDate: form.scheduledDate || null,
            scheduledStart: form.scheduledDate && form.scheduledStart ? form.scheduledStart : null,
            scheduledEnd: form.scheduledDate && form.scheduledStart && form.scheduledEnd ? form.scheduledEnd : null,
            processedOn: form.processedOn || null,
            price: form.price === '' ? null : Number(form.price),
            parcelNumber: form.parcelNumber,
            cadastralZone: form.cadastralZone,
            municipality: form.municipality,
            ownerName: form.ownerName,
            coOwners: form.coOwners,
            propertySheet: form.propertySheet,
            investor: form.investor,
            parentParcel: form.parentParcel,
            newParcels: form.newParcels,
            latitude: form.location ? form.location.lat : null,
            longitude: form.location ? form.location.lng : null,
            cadastreCaseNumber: form.cadastreCaseNumber,
            submittedOn: form.submittedOn || null,
            includeParcelling: form.includeParcelling,
            assigneeIds: job ? null : form.assignees.map(a => a.id),
        };
        setSaving(true);
        try {
            const saved = job ? await JobService.update(job.id, payload) : await JobService.create(payload);
            showToast(job ? t('jobs.toast.updated') : saved.number ? t('jobs.toast.created', { number: saved.number }) : t('jobs.toast.createdNoNumber'), 'success');
            onSaved(saved);
        } catch (err) {
            const fe = fieldErrors(err);
            if (err?.response?.data?.code === 'CASE_NUMBER_TAKEN') setErrors(x => ({ ...x, number: errorMessage(err) }));
            else if (Object.keys(fe).length) setErrors(fe);
            else showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setSaving(false);
        }
    };

    const selectedCategory = categories.find(c => c.id === form.categoryId);

    // New jobs: suggest the name from category + KO + parcel ("Snimanje objekta – Sutomore 1234/2"). It keeps
    // following those fields until the user types a name of their own.
    const lastAutoTitle = useRef('');
    const categoryName = selectedCategory?.name;
    useEffect(() => {
        if (!open || job || !categoryName) return;
        const place = [form.cadastralZone.trim(), form.parcelNumber.trim()].filter(Boolean).join(' ');
        const suggestion = place ? `${categoryName} – ${place}` : categoryName;
        setForm(f => {
            if (f.title.trim() && f.title !== lastAutoTitle.current) return f;
            lastAutoTitle.current = suggestion;
            return f.title === suggestion ? f : { ...f, title: suggestion };
        });
    }, [open, job, categoryName, form.cadastralZone, form.parcelNumber]);

    const label = (text) => <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>{text}</Typography>;

    return (
        <Drawer anchor="right" open={open} onClose={saving ? undefined : onClose}
                PaperProps={{ sx: { width: section ? { xs: '100%', sm: 560 } : { xs: '100%', md: 'max(50vw, 880px)' }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #EEF2F6' }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#1a2332' }}>{section ? t(`jobs.editSection.${section}`) : job ? t('jobs.edit') : t('jobs.new')}</Typography>
                    {job && <Typography sx={{ fontSize: 12.5, color: '#94A3B8' }}>{job.number}</Typography>}
                </Box>
                <IconButton onClick={onClose} disabled={saving} aria-label={t('common.close')}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
                {/*
                  * Half-screen, two columns on desktop: left = job, client, description; right = survey details, dates; then
                  * status | price and location across. Phones keep the order agreed with the firm (case no., category, survey
                  * details, client, dates, description, status, price, location): both columns dissolve (display: contents)
                  * and `order` interleaves their sections.
                  */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: section ? '1fr' : '1fr 1fr' }, columnGap: 4, alignItems: 'start' }}>
                <Box sx={{ display: { xs: 'contents', md: 'block' }, order: 1 }}>
                <Box sx={{ order: 1, ...hide('details') }}>
                {label(t('jobs.sections.basics'))}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" label={t('jobs.fields.number')} value={form.number} onChange={set('number')}
                                   error={!!errors.number} helperText={errors.number || t('jobs.numberHint')}
                                   slotProps={{ htmlInput: { maxLength: 20 } }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        {nextNumber && !form.number.trim() && (
                            <Button size="small" variant="outlined" onClick={() => { setForm(f => ({ ...f, number: nextNumber })); setErrors(x => ({ ...x, number: undefined })); }}
                                    sx={{ mt: 0.5, whiteSpace: 'nowrap' }}>
                                {t('jobs.useNextNumber', { number: nextNumber })}
                            </Button>
                        )}
                    </Grid>
                    <Grid size={12}>
                        <TextField select fullWidth size="small" label={t('jobs.fields.category')} value={form.categoryId} onChange={set('categoryId')}>
                            <MenuItem value=""><em>{t('jobs.noCategory')}</em></MenuItem>
                            {categories.map(c => (
                                <MenuItem key={c.id} value={c.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: c.color }} />{c.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {selectedCategory?.workflow === 'BUILDING_PERMIT' && (
                        <Grid size={12}>
                            <FormControlLabel control={<Switch checked={form.includeParcelling} onChange={(e) => setForm(f => ({ ...f, includeParcelling: e.target.checked }))} />}
                                              label={<Typography sx={{ fontSize: 14 }}>{t('jobs.fields.includeParcelling')}</Typography>} />
                        </Grid>
                    )}
                </Grid>

                </Box>

                <Box sx={{ order: 3, ...hide('details') }}>
                {label(t('jobs.sections.client'))}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={12}>
                        <ClientPicker value={form.client} onChange={(c) => { setForm(f => ({ ...f, client: c })); setErrors(x => ({ ...x, clientId: undefined })); }}
                                      error={!!errors.clientId} helperText={errors.clientId} />
                    </Grid>
                    {/* Building surveys only; the client is offered, never filled in by itself. */}
                    {hasInvestor(selectedCategory) && (
                        <Grid size={12}>
                            <TextField fullWidth size="small" label={t('jobs.fields.investor')} placeholder={t('survey.ownerHint')} value={form.investor}
                                       onChange={set('investor')} slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 150 } }} />
                            {form.client?.name && !form.investor.trim() && (
                                <Button size="small" variant="outlined" startIcon={<PersonOutlineIcon />} sx={{ mt: 1 }}
                                        onClick={() => setForm(f => ({ ...f, investor: f.client.name }))}>
                                    {t('survey.useClient', { name: form.client.name })}
                                </Button>
                            )}
                        </Grid>
                    )}
                    <Grid size={12}>
                        <TextField fullWidth size="small" required label={t('jobs.fields.title')} placeholder={t('jobs.fields.titlePlaceholder')}
                                   value={form.title} onChange={set('title')} error={!!errors.title} helperText={errors.title} />
                    </Grid>
                </Grid>

                </Box>

                <Box sx={{ order: 5, mb: 3, ...hide('details') }}>
                    <TextField fullWidth size="small" multiline minRows={section ? 8 : 4} label={t('jobs.fields.description')}
                               value={form.description} onChange={set('description')} />
                </Box>
                </Box>

                <Box sx={{ display: { xs: 'contents', md: 'block' }, order: 2 }}>
                <Box sx={{ order: 2, ...hide('survey') }}>
                {label(`${t('jobs.sections.survey')} · ${t('common.optional')}`)}
                <Box sx={{ mb: 3 }}>
                    <SurveyFields values={form} client={form.client} onChange={(patch) => setForm(f => ({ ...f, ...patch }))} />
                    {/* Subdivision and merger: several parcels can be listed, comma separated. */}
                    {hasParcelNumbers(selectedCategory) && (
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField fullWidth size="small" label={t('jobs.fields.parentParcel')} placeholder={t('survey.parcelsPlaceholder')}
                                           value={form.parentParcel} onChange={set('parentParcel')}
                                           slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 200 } }} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField fullWidth size="small" label={t('jobs.fields.newParcels')} placeholder={t('survey.parcelsPlaceholder')}
                                           value={form.newParcels} onChange={set('newParcels')}
                                           slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 500 } }} />
                            </Grid>
                        </Grid>
                    )}
                </Box>
                </Box>

                <Box sx={{ order: 4, ...hide('details') }}>
                {label(t('jobs.sections.dates'))}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* The planned date is for the work on site (planner); the label names the category's field step. */}
                    <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                        <TextField fullWidth size="small" type="date" label={scheduledLabel(t, selectedCategory)} value={form.scheduledDate}
                                   onChange={set('scheduledDate')} slotProps={{ inputLabel: { shrink: true } }} />
                    </Grid>
                    {/* Optional time slot of the field work (planner); needs the date. */}
                    <Grid size={6}>
                        <TextField fullWidth size="small" type="time" label={t('jobs.fields.scheduledStart')} value={form.scheduledStart}
                                   onChange={set('scheduledStart')} disabled={!form.scheduledDate}
                                   slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 900 } }} />
                    </Grid>
                    <Grid size={6}>
                        <TextField fullWidth size="small" type="time" label={t('jobs.fields.scheduledEnd')} value={form.scheduledEnd}
                                   onChange={set('scheduledEnd')} disabled={!form.scheduledDate || !form.scheduledStart}
                                   error={!!errors.scheduledEnd} helperText={errors.scheduledEnd}
                                   slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 900 } }} />
                    </Grid>
                    {/* Optional: when the case was worked up in the office (elaborate prepared). */}
                    <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                        <TextField fullWidth size="small" type="date" label={t('jobs.fields.processedOn')} value={form.processedOn}
                                   onChange={set('processedOn')} slotProps={{ inputLabel: { shrink: true } }} />
                    </Grid>
                    {/* Every category (the firm's wish), both optional. In cadastre categories the date is the checklist's
                        "submitted" step. */}
                    <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                        <TextField fullWidth size="small" type="date" label={t('jobs.fields.submittedOn')} value={form.submittedOn}
                                   onChange={set('submittedOn')} slotProps={{ inputLabel: { shrink: true } }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                        <TextField fullWidth size="small" label={t('jobs.fields.cadastreCaseNumber')} value={form.cadastreCaseNumber}
                                   onChange={set('cadastreCaseNumber')} slotProps={{ htmlInput: { maxLength: 60 } }} />
                    </Grid>
                    {!job && (
                        <Grid size={12}>
                            {/* Existing jobs change their crew on the job page. */}
                            <Autocomplete multiple size="small" options={team} value={form.assignees}
                                          isOptionEqualToValue={(a, b) => a.id === b.id}
                                          getOptionLabel={(u) => `${u.firstName} ${u.lastName}`}
                                          onChange={(_, v) => setForm(f => ({ ...f, assignees: v }))}
                                          renderInput={(p) => <TextField {...p} label={t('jobs.fields.assignees')} helperText={t('jobs.assigneesHint')} />} />
                        </Grid>
                    )}
                </Grid>
                </Box>
                </Box>

                <Box sx={{ order: 6, ...hide('all') }}>
                {label(t('jobs.sections.status'))}
                <Box sx={{ mb: 3 }}><StatusSegmented value={form.status} onChange={(s) => setForm(f => ({ ...f, status: s }))} /></Box>
                </Box>

                {isAdmin && <Box sx={{ order: 7, ...hide('all') }}>
                {label(t('jobs.sections.price'))}
                {/* A new job's price can be typed straight away; an existing one only once shown on the job page. */}
                {job && !money?.visible && (
                    <Box sx={{ mb: 3 }}>
                        <Button size="small" variant="outlined" startIcon={<LockOutlinedIcon />} onClick={money?.show}>{t('money.showToEdit')}</Button>
                    </Box>
                )}
                {(!job || money?.visible) && <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" type="number" label={t('jobs.fields.price')} value={form.price} onChange={set('price')}
                                   error={!!errors.price} helperText={errors.price}
                                   slotProps={{ input: { startAdornment: <InputAdornment position="start">€</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }} />
                    </Grid>
                </Grid>}
                </Box>}

                <Box sx={{ order: 8, gridColumn: '1 / -1', ...hide('all') }}>
                {/* Optional location: pin on the map, current position, or typed survey coordinates. */}
                <Box onClick={() => setShowLocation(s => !s)} sx={{
                    display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none', p: 1.5,
                    borderRadius: '12px', bgcolor: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.14)',
                }}>
                    <PlaceOutlinedIcon sx={{ color: '#EF4444', fontSize: 20 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1a2332' }}>
                            {t('location.title')}{form.location ? ` · ${t('location.set')}` : ''}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#64748B' }}>{t('location.hint')}</Typography>
                    </Box>
                    <ExpandMoreIcon sx={{ color: '#94A3B8', transform: showLocation ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </Box>
                <Collapse in={showLocation} unmountOnExit>
                    <Box sx={{ mt: 1.5 }}>
                        <LocationPicker value={form.location} onChange={(loc) => setForm(f => ({ ...f, location: loc }))} />
                    </Box>
                </Collapse>
                </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, py: 2, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC' }}>
                <Button onClick={onClose} disabled={saving} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving} disableElevation sx={{ minWidth: 120 }}>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : (job ? t('common.save') : t('jobs.create'))}
                </Button>
            </Box>
        </Drawer>
    );
}
